/**
 * What Today offers before anything is ticked (B-34, reworked in B-49 / D-100).
 *
 * Four answers, in order of precedence:
 *   · a plan, and today is one of its days → that day, weights from history
 *   · a plan, and today is not → a rest day
 *   · no plan, and this weekday has been trained → last time on this weekday, exactly
 *   · otherwise → nothing, and Today offers to make a plan or just log
 *
 * The third rule is the one B-34 considered and rejected (D-87): measured against fifteen
 * real days, cloning the last same-weekday session lets one outlier Monday of chest work
 * stand in for four leg Mondays, which a frequency rule smoothed over. The user asked for
 * it anyway, and D-100 records the trade: it is predictable — the header names the date
 * it came from — and anyone who wants better than "what I did last Monday" now has a
 * plan, which replaces this rule entirely.
 *
 * The sets returned are proposals to be corrected, never a log. Nothing here writes; the
 * caller stores a set only when the user ticks it (D-78: a wrong number that has been
 * stored looks exactly like a real one).
 *
 * Imports nothing beyond core and reads no clock: `today` arrives resolved, and `newId`
 * is passed in and called with the exercise and the set's position so a caller can
 * derive `` `proposed:${catalogId}:${index}` `` — stable across every rebuild, which is
 * what lets a ticked set cancel its own re-proposal after a remount (B-35).
 */
import { dayOfWeek } from "./dates.js";
export function suggestedExercises(suggestion) {
    return suggestion.kind === "plan" || suggestion.kind === "lastWeekday" ? suggestion.exercises : [];
}
export function todaySuggestion(plan, workouts, today, newId) {
    const weekday = dayOfWeek(today);
    // Today, and anything after it, is not history. Today's own sets must never become
    // "last time" — the first tick of a session would otherwise offer itself back.
    const past = [...workouts].filter((w) => w.date < today).sort((a, b) => (a.date < b.date ? 1 : -1));
    if (plan) {
        const planDay = plan.days[weekday];
        if (!planDay)
            return { kind: "restDay" };
        return {
            kind: "plan",
            label: planDay.label,
            exercises: planDay.exercises.map((planned) => {
                const last = lastOccurrence(past, planned.catalogId);
                const weightKg = last?.sets.at(-1)?.weightKg ?? 0;
                return {
                    catalogId: planned.catalogId,
                    name: planned.name,
                    muscleGroup: planned.muscleGroup,
                    sets: Array.from({ length: planned.sets }, (_, i) => ({
                        id: newId(planned.catalogId, i),
                        reps: planned.targetReps,
                        weightKg,
                        supported: false,
                    })),
                    from: last?.date ?? null,
                };
            }),
        };
    }
    // A day with no sets (an empty PT day, D-107) has nothing to copy.
    const lastTime = past.find((w) => dayOfWeek(w.date) === weekday && w.exercises.some((e) => e.sets.length > 0));
    if (!lastTime)
        return { kind: "empty" };
    // An exercise logged twice that day becomes one, sets in order. Ids are exercise and
    // position, so two entries for one exercise would otherwise mint the same ids twice.
    const joined = new Map();
    for (const exercise of lastTime.exercises) {
        const held = joined.get(exercise.catalogId);
        if (held)
            held.sets.push(...exercise.sets);
        else
            joined.set(exercise.catalogId, { exercise, sets: [...exercise.sets] });
    }
    return {
        kind: "lastWeekday",
        from: lastTime.date,
        exercises: [...joined.values()].map(({ exercise, sets }) => ({
            catalogId: exercise.catalogId,
            name: exercise.name,
            muscleGroup: exercise.muscleGroup,
            sets: sets.map((s, i) => ({
                id: newId(exercise.catalogId, i),
                reps: s.reps,
                weightKg: s.weightKg,
                supported: s.supported,
            })),
            from: lastTime.date,
        })),
    };
}
/** The most recent logged occurrence of an exercise, newest-first history assumed. */
function lastOccurrence(pastNewestFirst, catalogId) {
    for (const workout of pastNewestFirst) {
        const found = [...workout.exercises].reverse().find((e) => e.catalogId === catalogId);
        if (found && found.sets.length > 0)
            return { date: workout.date, sets: found.sets };
    }
    return null;
}
//# sourceMappingURL=suggest.js.map